import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  ConfidenceLabel,
  ManualJobMatchButton,
  StatusBadge,
} from "@/accounts-payable/common";
import type { ApInvoice } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  formatApDate,
  invoiceStatusMeta,
  stJobUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
export const InvoiceReviewHeader = ({
  invoice,
  businessId,
}: {
  invoice: ApInvoice;
  businessId: string | null;
}) => (
  <Stack gap="xs" fullWidth>
    <Stack direction="row" gap="sm" align="center" className="flex-wrap">
      <Typography variant="titleW3">{invoice.vendor_name}</Typography>
      <StatusBadge meta={invoiceStatusMeta(invoice)} />
      {/* The confidence qualifies the match, so it sits with the match
                status, not in the metadata line (which stays one size). */}
      {invoice.st_job_external_id && invoice.job_match_confidence != null && (
        <ConfidenceLabel value={invoice.job_match_confidence} />
      )}
    </Stack>
    {/* Styled like the Review tab card's line (one size, one colour, the
              job as an underlined ST link, the total set off at the end), but
              dated by the invoice itself: this page is about the document, the
              queue is about arrival. */}
    <Stack
      direction="row"
      gap="sm"
      align="center"
      className="flex-wrap text-md text-tertiary-text"
    >
      <span>{invoice.invoice_number}</span>
      {invoice.st_job_external_id ? (
        <>
          <span aria-hidden>·</span>
          <ServiceTitanIconLink
            url={stJobUrl(invoice.st_job_external_id)}
            label={`Open job #${invoice.st_job_external_id} in ServiceTitan`}
            className="ml-0 underline underline-offset-2"
          >
            Job #{invoice.st_job_external_id}
          </ServiceTitanIconLink>
        </>
      ) : (
        canManuallyMatchJob(invoice) && (
          <>
            <span aria-hidden>·</span>
            <span>No job</span>
          </>
        )
      )}
      <span aria-hidden>·</span>
      <span>{formatApDate(invoice.invoice_date)}</span>
      {invoice.total_amount_cents ? (
        <>
          <span className="mx-1 h-5 w-px bg-stroke-tertiary" aria-hidden />
          <span
            className={cn("font-semibold tabular-nums", {
              "text-negative-text": invoice.total_amount_cents < 0,
              "text-primary-text": invoice.total_amount_cents >= 0,
            })}
          >
            {formatCentsOrDashIfZero(invoice.total_amount_cents)}
          </span>
        </>
      ) : null}
      {canManuallyMatchJob(invoice) && (
        <ManualJobMatchButton
          businessId={businessId}
          documentId={invoice.id}
          agentRunId={invoice.agent_run_id}
          label={invoice.st_job_external_id ? "Change job" : undefined}
          className="ml-2"
        />
      )}
    </Stack>
  </Stack>
);
